import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import { listFiles } from '../../../helpers/storage';

const MoveToModal = ({
  open,
  onClose,
  onMove,
  items = [],
  currentPath = '',
  loading,
}) => {
  const [selectedPath, setSelectedPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [browsePath, setBrowsePath] = useState('');
  const [browseLoading, setBrowseLoading] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  // Get paths that cannot be selected (current location and items being moved)
  const excludedPaths = useMemo(() => new Set([
    currentPath,
    ...items.map((item) => item.path),
  ]), [currentPath, items]);

  // Load folders for current browse path
  const loadFolders = useCallback(async (path) => {
    setBrowseLoading(true);
    try {
      const response = await listFiles(path);
      const data = response.data;
      // Filter out items being moved
      const filteredFolders = (data.folders || []).filter(
        (folder) => !excludedPaths.has(folder.path)
      );
      setFolders(filteredFolders);
    } catch (error) {
      console.error('Failed to load folders:', error);
      setFolders([]);
    } finally {
      setBrowseLoading(false);
    }
  }, [excludedPaths]);

  // Load folders when modal opens or browse path changes
  useEffect(() => {
    if (open) {
      loadFolders(browsePath);
      setSelectedPath(browsePath);

      // Build breadcrumbs
      if (!browsePath) {
        setBreadcrumbs([]);
      } else {
        const parts = browsePath.split('/').filter(Boolean);
        const crumbs = parts.map((part, index) => ({
          name: part,
          path: parts.slice(0, index + 1).join('/') + '/',
        }));
        setBreadcrumbs(crumbs);
      }
    }
  }, [open, browsePath, loadFolders]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setBrowsePath('');
      setSelectedPath('');
      setFolders([]);
      setBreadcrumbs([]);
    }
  }, [open]);

  const handleFolderClick = (folderPath) => {
    setBrowsePath(folderPath);
  };

  const handleNavigateToRoot = () => {
    setBrowsePath('');
  };

  const handleNavigateToBreadcrumb = (path) => {
    setBrowsePath(path);
  };

  const handleMove = () => {
    onMove(selectedPath);
  };

  const itemCount = items.length;
  const itemLabel = itemCount === 1
    ? items[0].name
    : `${itemCount} items`;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        variant="outlined"
        role="dialog"
        aria-labelledby="move-modal-title"
        sx={{ minWidth: 480, maxHeight: '80vh' }}
      >
        <DialogTitle id="move-modal-title">
          <Stack direction="row" alignItems="center" gap={1}>
            <DriveFileMoveOutlinedIcon />
            Move {itemLabel}
          </Stack>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ p: 0 }}>
          {/* Breadcrumbs */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.level1' }}>
            <Breadcrumbs separator="›" size="sm">
              <Link
                component="button"
                color={browsePath ? 'neutral' : 'primary'}
                onClick={handleNavigateToRoot}
                underline="hover"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <HomeIcon fontSize="small" />
                Root
              </Link>
              {breadcrumbs.map((crumb, index) => (
                <Link
                  key={crumb.path}
                  component="button"
                  color={index === breadcrumbs.length - 1 ? 'primary' : 'neutral'}
                  onClick={() => handleNavigateToBreadcrumb(crumb.path)}
                  underline="hover"
                >
                  {crumb.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>

          <Divider />

          {/* Folder list */}
          <Box sx={{ minHeight: 200, maxHeight: 300, overflow: 'auto' }}>
            {browseLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress size="sm" />
              </Stack>
            ) : folders.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                <Typography level="body-sm" color="neutral">
                  No subfolders
                </Typography>
              </Stack>
            ) : (
              <List size="sm" sx={{ '--ListItem-paddingY': '8px' }}>
                {folders.map((folder) => (
                  <ListItem key={folder.path}>
                    <ListItemButton
                      onClick={() => handleFolderClick(folder.path)}
                      sx={{ borderRadius: 'sm' }}
                    >
                      <ListItemDecorator>
                        <FolderIcon sx={{ color: '#ffc107' }} />
                      </ListItemDecorator>
                      {folder.name}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          <Divider />

          {/* Selected destination */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.level1' }}>
            <Typography level="body-sm" color="neutral">
              Move to:{' '}
              <Typography component="span" fontWeight={600} color="primary">
                {selectedPath || 'Root'}
              </Typography>
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            variant="solid"
            color="primary"
            onClick={handleMove}
            loading={loading}
            startDecorator={<DriveFileMoveOutlinedIcon />}
          >
            Move here
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default MoveToModal;
